/* Novus Invictus Command Center: track-your-order widget.
 * Vanilla JS, no dependencies. Talks to the public track API (POST for
 * email/zip lookups so no PII lands in URLs; GET only for signed deep
 * links). All DOM nodes are built with textContent, never innerHTML
 * with response data. Installed as assets/ni-track.js by
 * lib/shopify/track-page.ts.
 *
 * Design: the signature element is the journey bar (Ordered > Shipped >
 * In transit > Delivered) with a track that fills to the parcel's
 * current position. Typography and color stay theme-neutral so the
 * widget reads as native inside every store theme. */
(function () {
  "use strict";

  var root = document.getElementById("ni-track");
  if (!root) return;

  var API = root.getAttribute("data-api") || "";
  var STORE = root.getAttribute("data-store") || "";
  var LOCALE = (root.getAttribute("data-locale") || "en").toLowerCase();
  var CONTACT = root.getAttribute("data-contact") || "";
  // Shopify hands us the request locale ("fr", "fr-FR", "de-DE"). Match
  // on the language subtag and fall back to English for anything we do
  // not translate.
  var LANG =
    LOCALE.indexOf("de") === 0 ? "de" : LOCALE.indexOf("fr") === 0 ? "fr" : "en";
  // The locale to format dates, times and country names in.
  var INTL_LOCALE =
    LANG === "de" ? "de-DE" : LANG === "fr" ? "fr-FR" : "en-US";

  var STRINGS = {
    en: {
      orderLabel: "Order number",
      orderPlaceholder: "e.g. #1234",
      emailLabel: "Email address",
      zipLabel: "Postal code",
      useZip: "Use postal code instead",
      useEmail: "Use email address instead",
      submit: "Track my order",
      formHint: "Enter the order number from your confirmation email.",
      loading: "Looking up your order...",
      notFound:
        "We could not find that order. Check the order number and try again.",
      genericError: "Something went wrong. Please try again in a minute.",
      orderEyebrow: "Order",
      placed: "Placed",
      shipmentOf: function (i, n) {
        return "Shipment " + i + " of " + n;
      },
      stages: ["Ordered", "Shipped", "In transit", "Delivered"],
      headline: {
        PENDING: "Getting your parcel ready",
        INFO_RECEIVED: "Handed to the carrier",
        IN_TRANSIT: "On its way to you",
        IN_TRANSIT_PRE: "On its way to your country",
        OUT_FOR_DELIVERY: "Out for delivery",
        READY_FOR_PICKUP: "Ready for pickup",
        DELIVERED: "Delivered",
        FAILED_ATTEMPT: "Delivery attempted",
        EXCEPTION: "Delivery needs attention",
        EXPIRED: "On its way to you",
      },
      subline: {
        PENDING:
          "The shipment is registered. Tracking starts at the first carrier scan.",
        IN_TRANSIT_PRE:
          "Detailed tracking events appear once your local carrier receives the parcel.",
        FAILED_ATTEMPT:
          "The carrier will try again or leave instructions for pickup.",
        EXCEPTION: "Check the latest update below or contact us for help.",
      },
      estimated: "Estimated delivery",
      carrier: "Carrier",
      tracking: "Tracking number",
      items: "In this shipment",
      trackWithCarrier: "Track with carrier",
      updates: "Tracking history",
      showAll: function (n) {
        return "Show all " + n + " updates";
      },
      showLess: "Show fewer updates",
      today: "Today",
      yesterday: "Yesterday",
      stageStatus: {
        done: "Done",
        current: ["Order received", "Shipped", "On its way", "Arrived"],
        next: "Upcoming",
      },
      nextTitle: "What happens next",
      nextSteps: [
        "Your parcel leaves our warehouse and gets its first carrier scan.",
        "It travels to your country. We update every scan here.",
        "Your local carrier takes over for the final delivery.",
        "Delivered to your door.",
      ],
      expectedTitle: "Expected delivery",
      enRouteTo: function (country) {
        return country ? "Heading to " + country : "On its way to you";
      },
      deliveredIn: function (place) {
        return "Delivered in " + place;
      },
      lateTitle: "Taking a little longer than expected",
      lateBody:
        "Your parcel has passed its usual delivery window. Most delayed parcels still arrive within a few days. Want us to look into it? Email us and we will check with the carrier.",
      quietNote: function (n) {
        return (
          "No new scans for " +
          n +
          " days. International legs can be quiet for a few days at a time."
        );
      },
      contactTitle: "Questions about your order?",
      contactCta: "Email us",
      mailSubject: function (order) {
        return "Question about order " + order;
      },
      mailSubjectGeneric: "Question about my order",
    },
    de: {
      orderLabel: "Bestellnummer",
      orderPlaceholder: "z. B. #1234",
      emailLabel: "E-Mail-Adresse",
      zipLabel: "Postleitzahl",
      useZip: "Stattdessen Postleitzahl verwenden",
      useEmail: "Stattdessen E-Mail-Adresse verwenden",
      submit: "Bestellung verfolgen",
      formHint: "Die Bestellnummer steht in Ihrer Bestellbestaetigung.",
      loading: "Bestellung wird gesucht...",
      notFound:
        "Wir konnten diese Bestellung nicht finden. Bitte Bestellnummer pruefen und erneut versuchen.",
      genericError:
        "Etwas ist schiefgelaufen. Bitte in einer Minute erneut versuchen.",
      orderEyebrow: "Bestellung",
      placed: "Bestellt am",
      shipmentOf: function (i, n) {
        return "Sendung " + i + " von " + n;
      },
      stages: ["Bestellt", "Versandt", "Unterwegs", "Zugestellt"],
      headline: {
        PENDING: "Ihre Sendung wird vorbereitet",
        INFO_RECEIVED: "An den Versanddienst uebergeben",
        IN_TRANSIT: "Auf dem Weg zu Ihnen",
        IN_TRANSIT_PRE: "Auf dem Weg in Ihr Land",
        OUT_FOR_DELIVERY: "In Zustellung",
        READY_FOR_PICKUP: "Abholbereit",
        DELIVERED: "Zugestellt",
        FAILED_ATTEMPT: "Zustellversuch",
        EXCEPTION: "Zustellung braucht Aufmerksamkeit",
        EXPIRED: "Auf dem Weg zu Ihnen",
      },
      subline: {
        PENDING:
          "Die Sendung ist angemeldet. Das Tracking beginnt beim ersten Scan.",
        IN_TRANSIT_PRE:
          "Detaillierte Ereignisse erscheinen, sobald Ihr lokaler Zusteller das Paket uebernimmt.",
        FAILED_ATTEMPT:
          "Der Zusteller versucht es erneut oder hinterlaesst eine Nachricht.",
        EXCEPTION:
          "Pruefen Sie das letzte Update unten oder kontaktieren Sie uns.",
      },
      estimated: "Voraussichtliche Zustellung",
      carrier: "Versanddienst",
      tracking: "Sendungsnummer",
      items: "In dieser Sendung",
      trackWithCarrier: "Beim Versanddienst verfolgen",
      updates: "Sendungsverlauf",
      showAll: function (n) {
        return "Alle " + n + " Ereignisse anzeigen";
      },
      showLess: "Weniger anzeigen",
      today: "Heute",
      yesterday: "Gestern",
      stageStatus: {
        done: "Erledigt",
        current: ["Bestellung erhalten", "Versandt", "Unterwegs", "Angekommen"],
        next: "Ausstehend",
      },
      nextTitle: "Wie es weitergeht",
      nextSteps: [
        "Ihr Paket verlaesst unser Lager und erhaelt den ersten Scan.",
        "Es reist in Ihr Land. Jeder Scan erscheint hier.",
        "Ihr lokaler Zusteller uebernimmt die letzte Meile.",
        "Zustellung an Ihre Tuer.",
      ],
      expectedTitle: "Voraussichtliche Zustellung",
      enRouteTo: function (country) {
        return country
          ? "Unterwegs nach " + country
          : "Im internationalen Versand";
      },
      deliveredIn: function (place) {
        return "Zugestellt in " + place;
      },
      lateTitle: "Dauert etwas laenger als erwartet",
      lateBody:
        "Ihre Sendung hat das uebliche Zustellfenster ueberschritten. Die meisten verspaeteten Pakete kommen dennoch innerhalb weniger Tage an. Schreiben Sie uns eine E-Mail, dann fragen wir beim Versanddienst nach.",
      quietNote: function (n) {
        return (
          "Seit " +
          n +
          " Tagen keine neuen Scans. Internationale Etappen koennen einige Tage still sein."
        );
      },
      contactTitle: "Fragen zu Ihrer Bestellung?",
      contactCta: "E-Mail schreiben",
      mailSubject: function (order) {
        return "Frage zu Bestellung " + order;
      },
      mailSubjectGeneric: "Frage zu meiner Bestellung",
    },
    fr: {
      orderLabel: "Numéro de commande",
      orderPlaceholder: "ex. #1234",
      emailLabel: "Adresse e-mail",
      zipLabel: "Code postal",
      useZip: "Utiliser le code postal",
      useEmail: "Utiliser l'adresse e-mail",
      submit: "Suivre ma commande",
      formHint:
        "Saisissez le numéro de commande figurant dans votre e-mail de confirmation.",
      loading: "Recherche de votre commande...",
      notFound:
        "Nous n'avons pas trouvé cette commande. Vérifiez le numéro et réessayez.",
      genericError:
        "Une erreur est survenue. Merci de réessayer dans une minute.",
      orderEyebrow: "Commande",
      placed: "Commandée le",
      shipmentOf: function (i, n) {
        return "Colis " + i + " sur " + n;
      },
      stages: ["Commandée", "Expédié", "En transit", "Livré"],
      headline: {
        PENDING: "Préparation de votre colis",
        INFO_RECEIVED: "Remis au transporteur",
        IN_TRANSIT: "En route vers vous",
        IN_TRANSIT_PRE: "En route vers votre pays",
        OUT_FOR_DELIVERY: "En cours de livraison",
        READY_FOR_PICKUP: "Prêt pour le retrait",
        DELIVERED: "Livré",
        FAILED_ATTEMPT: "Tentative de livraison",
        EXCEPTION: "La livraison demande votre attention",
        EXPIRED: "En route vers vous",
      },
      subline: {
        PENDING:
          "Le colis est enregistré. Le suivi démarre au premier scan du transporteur.",
        IN_TRANSIT_PRE:
          "Les événements détaillés apparaissent dès que votre transporteur local reçoit le colis.",
        FAILED_ATTEMPT:
          "Le transporteur fera une nouvelle tentative ou laissera des instructions de retrait.",
        EXCEPTION:
          "Consultez la dernière mise à jour ci-dessous ou contactez-nous.",
      },
      estimated: "Livraison estimée",
      carrier: "Transporteur",
      tracking: "Numéro de suivi",
      items: "Dans ce colis",
      trackWithCarrier: "Suivre chez le transporteur",
      updates: "Historique du suivi",
      showAll: function (n) {
        return "Afficher les " + n + " mises à jour";
      },
      showLess: "Afficher moins",
      today: "Aujourd'hui",
      yesterday: "Hier",
      stageStatus: {
        done: "Terminé",
        current: ["Commande reçue", "Expédié", "En chemin", "Arrivé"],
        next: "À venir",
      },
      nextTitle: "Les prochaines étapes",
      nextSteps: [
        "Votre colis quitte notre entrepôt et reçoit son premier scan.",
        "Il voyage vers votre pays. Chaque scan apparaît ici.",
        "Votre transporteur local prend le relais pour la livraison finale.",
        "Livré chez vous.",
      ],
      expectedTitle: "Livraison prévue",
      enRouteTo: function (country) {
        return country ? "En route vers " + country : "En route vers vous";
      },
      deliveredIn: function (place) {
        return "Livré à " + place;
      },
      lateTitle: "Un peu plus long que prévu",
      lateBody:
        "Votre colis a dépassé son délai de livraison habituel. La plupart des colis en retard arrivent malgré tout sous quelques jours. Vous voulez que nous vérifiions ? Écrivez-nous et nous contacterons le transporteur.",
      quietNote: function (n) {
        return (
          "Aucun nouveau scan depuis " +
          n +
          " jours. Les trajets internationaux peuvent rester silencieux plusieurs jours."
        );
      },
      contactTitle: "Une question sur votre commande ?",
      contactCta: "Écrivez-nous",
      mailSubject: function (order) {
        return "Question concernant la commande " + order;
      },
      mailSubjectGeneric: "Question concernant ma commande",
    },
  };
  var T = STRINGS[LANG];

  // Journey stage per status: 0 Ordered, 1 Shipped, 2 In transit,
  // 3 Delivered. "warn" marks the current stage amber.
  var STAGE_BY_STATUS = {
    PENDING: { stage: 0, warn: false },
    INFO_RECEIVED: { stage: 1, warn: false },
    IN_TRANSIT: { stage: 2, warn: false },
    EXPIRED: { stage: 2, warn: false },
    OUT_FOR_DELIVERY: { stage: 2, warn: false },
    READY_FOR_PICKUP: { stage: 2, warn: false },
    FAILED_ATTEMPT: { stage: 2, warn: true },
    EXCEPTION: { stage: 2, warn: true },
    DELIVERED: { stage: 3, warn: false },
  };


  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function clear() {
    while (root.firstChild) root.removeChild(root.firstChild);
  }

  function formatDate(iso) {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString(
        INTL_LOCALE,
        { year: "numeric", month: "long", day: "numeric" },
      );
    } catch {
      return "";
    }
  }

  // Static inline icons (no user data ever flows through innerHTML).
  var ICONS = {
    ordered:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="5.5" y="8" width="13" height="11.5" rx="2"/><path d="M9 8.5V7a3 3 0 0 1 6 0v1.5"/></svg>',
    shipped:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4.5 8.2 12 4.5l7.5 3.7v7.6L12 19.5l-7.5-3.7V8.2z"/><path d="M4.5 8.2 12 12l7.5-3.8"/><path d="M12 12v7.5"/></svg>',
    transit:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 7.5h10.5v8H2.5z"/><path d="M13 10.5h3.8l2.7 2.7v2.3H13"/><circle cx="6.5" cy="17.5" r="1.7"/><circle cx="16" cy="17.5" r="1.7"/></svg>',
    delivered:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 10.5 12 3l9 7.5"/><path d="M5.5 9.5V21h13V9.5"/><path d="M9.5 21v-6h5v6"/></svg>',
    pin:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 21s-6.5-5.6-6.5-10a6.5 6.5 0 0 1 13 0c0 4.4-6.5 10-6.5 10z"/><circle cx="12" cy="10.6" r="2.3"/></svg>',
    mail:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3.5" y="5.5" width="17" height="13" rx="2"/><path d="m4.5 7.5 7.5 5.5 7.5-5.5"/></svg>',
    check:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5.5 12.5l4.3 4.3L18.5 7"/></svg>',
    warn:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 6.5v7"/><path d="M12 17.2v.1"/></svg>',
  };
  var STAGE_ICONS = ["ordered", "shipped", "transit", "delivered"];

  function iconEl(name, className) {
    var span = el("span", className);
    span.innerHTML = ICONS[name] || "";
    return span;
  }

  function formatShortDate(iso) {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString(
        INTL_LOCALE,
        { month: "short", day: "numeric" },
      );
    } catch {
      return null;
    }
  }

  function countryName(code) {
    if (!code) return null;
    try {
      var names = new Intl.DisplayNames([INTL_LOCALE], {
        type: "region",
      });
      return names.of(code) || code;
    } catch {
      return code;
    }
  }

  // Latest visible (already origin-scrubbed) location; the CN leg never
  // reaches the browser, so pre-handoff this falls through to the
  // "En route to <destination>" wording.
  function currentLocation(shipment) {
    var cps = shipment.checkpoints || [];
    for (var i = 0; i < cps.length; i++) {
      if (cps[i] && cps[i].location) return titleCasePlace(cps[i].location);
    }
    return null;
  }

  function formatDayHeader(iso) {
    if (!iso) return "";
    try {
      var d = new Date(iso);
      var now = new Date();
      function ymd(x) {
        return x.getFullYear() + "-" + x.getMonth() + "-" + x.getDate();
      }
      if (ymd(d) === ymd(now)) return T.today;
      var y = new Date(now.getTime() - 86400000);
      if (ymd(d) === ymd(y)) return T.yesterday;
      return d.toLocaleDateString(INTL_LOCALE, {
        weekday: "long",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "";
    }
  }

  function formatTimeOnly(iso) {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleTimeString(
        INTL_LOCALE,
        { hour: "2-digit", minute: "2-digit" },
      );
    } catch {
      return "";
    }
  }

  function dayKey(iso) {
    if (!iso) return "unknown";
    try {
      var d = new Date(iso);
      return d.getFullYear() + "-" + d.getMonth() + "-" + d.getDate();
    } catch {
      return "unknown";
    }
  }

  function formatDateRange(fromIso, toIso) {
    var a = formatShortDate(fromIso);
    var b = formatShortDate(toIso);
    if (a && b) return a === b ? a : a + " \u2013 " + b;
    return a || b || "";
  }

  // Carrier feeds sometimes SHOUT the whole status. Sentence-case a
  // fully-uppercase description; leave mixed-case strings untouched.
  var DESC_ACRONYMS = ["USPS", "USA", "US", "UK", "EU", "DHL", "UPS"];
  function normalizeDescription(raw) {
    if (!raw) return raw;
    if (/[a-z]/.test(raw)) return raw;
    var lower = raw.toLowerCase();
    var sentence = lower.charAt(0).toUpperCase() + lower.slice(1);
    return sentence.replace(/\b[a-z]+\b/g, function (w) {
      return DESC_ACRONYMS.indexOf(w.toUpperCase()) > -1 ? w.toUpperCase() : w;
    });
  }

  function titleCasePlace(raw) {
    if (!raw) return "";
    // Carrier feeds shout locations ("SAN RAMON CA"); calm them down.
    return raw.replace(/\w[^\s,]*/g, function (word) {
      if (word.length <= 2) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    });
  }

  // ------------------------------------------------------------------
  // Form
  // ------------------------------------------------------------------

  function renderForm(prefillOrder, message) {
    clear();

    var card = el("div", "ni-card ni-form-card");
    if (message) card.appendChild(el("p", "ni-error", message));

    var form = el("form", "ni-form");

    var orderField = el("div", "ni-field");
    orderField.appendChild(el("label", "ni-label", T.orderLabel));
    var orderInput = el("input", "ni-input");
    orderInput.type = "text";
    orderInput.name = "order";
    orderInput.placeholder = T.orderPlaceholder;
    orderInput.required = true;
    orderInput.autocomplete = "off";
    if (prefillOrder) orderInput.value = prefillOrder;
    orderField.appendChild(orderInput);
    form.appendChild(orderField);

    var submit = el("button", "ni-submit", T.submit);
    submit.type = "submit";
    form.appendChild(submit);

    form.addEventListener("submit", function (evt) {
      evt.preventDefault();
      lookup({ store: STORE, order: orderInput.value }, orderInput.value);
    });

    card.appendChild(form);
    card.appendChild(el("p", "ni-hint", T.formHint));
    if (CONTACT) {
      var contactLink = el("a", "ni-form-contact", T.contactTitle);
      contactLink.href = contactHref(T.mailSubjectGeneric);
      card.appendChild(contactLink);
    }
    root.appendChild(card);
  }

  function renderLoading() {
    clear();
    var card = el("div", "ni-card ni-loading-card");
    card.appendChild(el("span", "ni-spinner"));
    card.appendChild(el("p", "ni-hint", T.loading));
    root.appendChild(card);
  }

  // ------------------------------------------------------------------
  // Result
  // ------------------------------------------------------------------

  var currentOrderNumber = "";

  function renderResult(data) {
    clear();
    currentOrderNumber = data.order.number || "";
    var wrap = el("div", "ni-result");

    var head = el("header", "ni-order-head");
    var headLeft = el("div", "ni-order-head-left");
    headLeft.appendChild(el("p", "ni-eyebrow", T.orderEyebrow));
    headLeft.appendChild(el("h2", "ni-order-number", data.order.number));
    head.appendChild(headLeft);
    if (data.order.placedAt) {
      var headRight = el("div", "ni-order-head-right");
      headRight.appendChild(el("p", "ni-eyebrow", T.placed));
      headRight.appendChild(
        el("p", "ni-order-date", formatDate(data.order.placedAt)),
      );
      head.appendChild(headRight);
    }
    wrap.appendChild(head);

    var shipments = data.shipments || [];
    for (var i = 0; i < shipments.length; i++) {
      wrap.appendChild(
        renderShipment(shipments[i], i, shipments.length, data.order),
      );
    }
    if (shipments.length === 0) {
      var empty = el("div", "ni-card");
      empty.appendChild(el("p", "ni-hint", T.headline.PENDING));
      wrap.appendChild(empty);
    }

    var contact = renderContact(data.order.number);
    if (contact) wrap.appendChild(contact);

    root.appendChild(wrap);
  }

  function renderJourney(shipment, order) {
    var status = shipment.status;
    var info = STAGE_BY_STATUS[status] || STAGE_BY_STATUS.IN_TRANSIT;
    var current = info.stage;
    var warn = info.warn;
    var delivered = status === "DELIVERED";

    // Milestone dates derived from what the API exposes: checkpoints
    // arrive newest-first. Best-effort; a missing date simply hides.
    var cps = shipment.checkpoints || [];
    var newest = cps.length > 0 ? cps[0].time : null;
    var oldest = cps.length > 0 ? cps[cps.length - 1].time : null;
    var dates = [
      order && order.placedAt ? order.placedAt : null,
      current >= 1 && !shipment.preHandoff ? oldest : null,
      current >= 2 && cps.length > 0 && !delivered ? newest : null,
      delivered ? newest : null,
    ];

    var journey = el("div", "ni-journey");
    if (warn) journey.className += " ni-journey-warn";
    if (delivered) journey.className += " ni-journey-delivered";
    journey.setAttribute("role", "img");
    journey.setAttribute(
      "aria-label",
      T.stages
        .map(function (label, idx) {
          return label + (idx === current ? " (current)" : "");
        })
        .join(", "),
    );

    var track = el("div", "ni-journey-track");
    var fill = el("div", "ni-journey-fill");
    fill.style.width = (current / (T.stages.length - 1)) * 100 + "%";
    track.appendChild(fill);
    journey.appendChild(track);

    var nodes = el("div", "ni-journey-nodes");
    for (var i = 0; i < T.stages.length; i++) {
      var state = i < current ? "done" : i === current ? "current" : "next";
      var node = el("div", "ni-journey-node ni-" + state);

      // Done + delivered-current get a check; a warn-current gets the
      // alert glyph; every other node shows its own stage icon so the
      // journey reads as bag -> box -> truck -> home.
      var iconName;
      if (state === "done") iconName = "check";
      else if (state === "current" && warn) iconName = "warn";
      else if (state === "current" && delivered) iconName = "check";
      else iconName = STAGE_ICONS[i];
      var circle = el("div", "ni-journey-circle");
      circle.appendChild(iconEl(iconName, "ni-journey-glyph"));
      node.appendChild(circle);

      node.appendChild(el("span", "ni-journey-label", T.stages[i]));

      // Status line: a real date when we have one, else a word for the
      // stage state (In progress / Upcoming).
      var dateLabel = formatShortDate(dates[i]);
      var statusText;
      if (dateLabel) statusText = dateLabel;
      else if (state === "current") statusText = T.stageStatus.current[i];
      else if (state === "next") statusText = T.stageStatus.next;
      else statusText = T.stageStatus.done;
      node.appendChild(el("span", "ni-journey-status", statusText));

      nodes.appendChild(node);
    }
    journey.appendChild(nodes);

    var place = currentLocation(shipment);
    var locText = null;
    if (delivered && place) locText = T.deliveredIn(place);
    else if (place) locText = place;
    else if (!delivered) locText = T.enRouteTo(countryName(shipment.destinationCountry));
    if (locText) {
      var loc = el("div", "ni-journey-loc-wrap");
      var chip = el("span", "ni-journey-loc");
      chip.appendChild(iconEl("pin", "ni-journey-loc-pin"));
      chip.appendChild(el("span", null, locText));
      loc.appendChild(chip);
      journey.appendChild(loc);
    }
    return journey;
  }

  function aggregateItems(items) {
    var byTitle = {};
    var order = [];
    for (var i = 0; i < (items || []).length; i++) {
      var item = items[i];
      if (!item || !item.title) continue;
      if (!Object.prototype.hasOwnProperty.call(byTitle, item.title)) {
        byTitle[item.title] = { quantity: 0, imageUrl: null };
        order.push(item.title);
      }
      byTitle[item.title].quantity += item.quantity || 1;
      if (!byTitle[item.title].imageUrl && item.imageUrl) {
        byTitle[item.title].imageUrl = item.imageUrl;
      }
    }
    return order.map(function (title) {
      return {
        title: title,
        quantity: byTitle[title].quantity,
        imageUrl: byTitle[title].imageUrl,
      };
    });
  }

  function headlineFor(shipment) {
    if (shipment.status === "IN_TRANSIT" && shipment.preHandoff) {
      return T.headline.IN_TRANSIT_PRE;
    }
    return T.headline[shipment.status] || T.headline.IN_TRANSIT;
  }

  function sublineFor(shipment) {
    if (shipment.status === "PENDING") return T.subline.PENDING;
    if (shipment.preHandoff && shipment.status !== "DELIVERED") {
      return T.subline.IN_TRANSIT_PRE;
    }
    if (shipment.status === "FAILED_ATTEMPT") return T.subline.FAILED_ATTEMPT;
    if (shipment.status === "EXCEPTION") return T.subline.EXCEPTION;
    return null;
  }

  function renderShipment(shipment, index, total, order) {
    var warn =
      shipment.status === "EXCEPTION" || shipment.status === "FAILED_ATTEMPT";
    var card = el("section", "ni-card ni-shipment");

    if (total > 1) {
      card.appendChild(
        el("p", "ni-eyebrow ni-shipment-count", T.shipmentOf(index + 1, total)),
      );
    }

    card.appendChild(renderJourney(shipment, order));

    var statusBlock = el("div", "ni-status");
    var headline = el(
      "h3",
      "ni-status-headline" +
        (shipment.status === "DELIVERED" ? " ni-status-delivered" : "") +
        (warn ? " ni-status-warn" : ""),
      headlineFor(shipment),
    );
    statusBlock.appendChild(headline);
    var sub = sublineFor(shipment);
    if (sub) statusBlock.appendChild(el("p", "ni-status-sub", sub));
    card.appendChild(statusBlock);

    if (shipment.expected) {
      var expectedText = shipment.expected.display
        ? shipment.expected.display
        : formatDateRange(shipment.expected.from, shipment.expected.to);
      if (expectedText) {
        var expected = el("div", "ni-expected");
        expected.appendChild(el("p", "ni-eyebrow", T.expectedTitle));
        expected.appendChild(el("p", "ni-expected-value", expectedText));
        card.appendChild(expected);
      }
    }

    if (shipment.late) {
      var notice = el("div", "ni-notice");
      notice.appendChild(el("p", "ni-notice-title", T.lateTitle));
      notice.appendChild(el("p", "ni-notice-body", T.lateBody));
      if (CONTACT) {
        var noticeBtn = el("a", "ni-notice-link", T.contactCta);
        noticeBtn.href = contactHref(T.mailSubject(currentOrderNumber || ""));
        notice.appendChild(noticeBtn);
      }
      card.appendChild(notice);
    }

    // Carrier name and tracking number are intentionally not shown to
    // the customer (operator decision): the linehaul carrier is generic
    // and the raw number is noise. Status, location, ETA and the
    // history below carry everything they need.

    var items = aggregateItems(shipment.items);
    if (items.length > 0) {
      var itemsWrap = el("div", "ni-items");
      itemsWrap.appendChild(el("p", "ni-eyebrow", T.items));
      var ul = el("ul", "ni-items-list");
      for (var i = 0; i < items.length; i++) {
        var li = el("li");
        if (items[i].imageUrl) {
          var img = document.createElement("img");
          img.className = "ni-item-thumb";
          img.src = items[i].imageUrl;
          img.alt = items[i].title;
          img.loading = "lazy";
          li.appendChild(img);
        } else {
          li.appendChild(iconEl("shipped", "ni-item-thumb ni-item-thumb-empty"));
        }
        var itemBody = el("div", "ni-item-body");
        itemBody.appendChild(el("p", "ni-item-title", items[i].title));
        itemBody.appendChild(el("p", "ni-item-qty", "× " + items[i].quantity));
        li.appendChild(itemBody);
        ul.appendChild(li);
      }
      itemsWrap.appendChild(ul);
      card.appendChild(itemsWrap);
    }

    var events = shipment.checkpoints || [];
    var hasHistory =
      events.length > 0 && !(shipment.preHandoff && events.length === 1);
    if (hasHistory) {
      card.appendChild(renderTimeline(events, shipment));
    } else if (shipment.status !== "DELIVERED") {
      card.appendChild(renderNextSteps());
    }

    return card;
  }

  function renderTimeline(events, shipment) {
    var status = shipment.status;
    var info = STAGE_BY_STATUS[status] || STAGE_BY_STATUS.IN_TRANSIT;
    var block = el("div", "ni-updates");
    if (status === "DELIVERED") block.className += " ni-updates-delivered";
    if (info.warn) block.className += " ni-updates-warn";
    block.appendChild(el("p", "ni-eyebrow", T.updates));
    if (shipment.quietDays != null) {
      block.appendChild(el("p", "ni-quiet", T.quietNote(shipment.quietDays)));
    }

    var list = el("div", "ni-timeline");

    // Events arrive newest-first. Group consecutive events by calendar
    // day so each day gets one heading, then a rail of timed rows.
    var groups = [];
    var currentKey = null;
    for (var j = 0; j < events.length; j++) {
      var key = dayKey(events[j].time);
      if (key !== currentKey) {
        currentKey = key;
        groups.push({ header: formatDayHeader(events[j].time), items: [] });
      }
      groups[groups.length - 1].items.push({ ev: events[j], globalIndex: j });
    }

    for (var g = 0; g < groups.length; g++) {
      var group = groups[g];
      var dayEl = el("div", "ni-day");
      if (group.header) {
        dayEl.appendChild(el("p", "ni-day-header", group.header));
      }
      var rows = el("ol", "ni-day-rows");
      for (var r = 0; r < group.items.length; r++) {
        var ev = group.items[r].ev;
        var isLatest = group.items[r].globalIndex === 0;
        var row = el("li", "ni-event" + (isLatest ? " ni-event-latest" : ""));

        var time = el("div", "ni-event-time", formatTimeOnly(ev.time) || "");
        row.appendChild(time);

        var iconName = isLatest
          ? info.warn
            ? "warn"
            : STAGE_ICONS[info.stage]
          : "check";
        row.appendChild(
          iconEl(
            iconName,
            "ni-event-dot" + (isLatest ? " ni-event-dot-latest" : ""),
          ),
        );

        var body = el("div", "ni-event-body");
        body.appendChild(
          el("p", "ni-event-desc", normalizeDescription(ev.description) || ""),
        );
        if (ev.location) {
          var place = el("p", "ni-event-loc");
          place.appendChild(iconEl("pin", "ni-event-loc-pin"));
          place.appendChild(
            el("span", null, titleCasePlace(ev.location)),
          );
          body.appendChild(place);
        }
        row.appendChild(body);
        rows.appendChild(row);
      }
      dayEl.appendChild(rows);
      list.appendChild(dayEl);
    }

    block.appendChild(list);
    return block;
  }

  // Pre-handoff / pending shipments have no history yet. Show the road
  // ahead so the card is never a dead end.
  function renderNextSteps() {
    var block = el("div", "ni-next");
    block.appendChild(el("p", "ni-eyebrow", T.nextTitle));
    var ol = el("ol", "ni-next-list");
    for (var i = 0; i < T.nextSteps.length; i++) {
      var li = el("li", "ni-next-step");
      li.appendChild(iconEl(STAGE_ICONS[i], "ni-next-icon"));
      li.appendChild(el("p", "ni-next-text", T.nextSteps[i]));
      ol.appendChild(li);
    }
    block.appendChild(ol);
    return block;
  }

  function contactHref(subject) {
    return (
      "mailto:" + CONTACT + "?subject=" + encodeURIComponent(subject)
    );
  }

  function renderContact(orderNumber) {
    if (!CONTACT) return null;
    var block = el("div", "ni-contact");
    block.appendChild(el("p", "ni-contact-title", T.contactTitle));
    var btn = el("a", "ni-contact-btn");
    btn.href = contactHref(
      orderNumber ? T.mailSubject(orderNumber) : T.mailSubjectGeneric,
    );
    btn.appendChild(iconEl("mail", "ni-contact-icon"));
    btn.appendChild(el("span", null, T.contactCta));
    block.appendChild(btn);
    return block;
  }

  // ------------------------------------------------------------------
  // Lookup
  // ------------------------------------------------------------------

  function lookup(payload, typedOrder) {
    renderLoading();
    var req;
    if (payload.k) {
      var qs =
        "?store=" +
        encodeURIComponent(payload.store) +
        "&order=" +
        encodeURIComponent(payload.order) +
        "&k=" +
        encodeURIComponent(payload.k);
      req = fetch(API + qs, { method: "GET" });
    } else {
      req = fetch(API, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
    }
    req
      .then(function (res) {
        if (res.status === 429) throw new Error("rate");
        return res.json();
      })
      .then(function (data) {
        if (data && data.found) renderResult(data);
        else renderForm(typedOrder || "", T.notFound);
      })
      .catch(function () {
        renderForm(typedOrder || "", T.genericError);
      });
  }

  var params = new URLSearchParams(window.location.search);
  var qOrder = params.get("order");
  var qKey = params.get("k");
  if (qOrder && qKey) {
    lookup({ store: STORE, order: qOrder, k: qKey }, qOrder);
  } else {
    renderForm(qOrder || "", null);
  }
})();
